import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Checkbox, Container, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";

import { config } from "config";
import { throwError } from "common/api/apiHelpers";
import { useSavedEvent } from "event/hooks/useSavedEvent";
import { useAddPlayerMutation } from "quiz/api/players.endpoints";
import type { PlayerCreationPayload } from "quiz/types/player.model";
import { PlayerFormFields } from "common/models/player.model";
import {
  FORM_VALIDATION_FIRST_NAME_REQUIRED,
  FORM_VALIDATION_LAST_NAME_REQUIRED,
  FORM_VALIDATION_EMAIL_NOT_VALIDATED,
  FORM_VALIDATION_PHONE_NOT_VALIDATED,
  FORM_VALIDATION_TERMS_OF_SERVICE,
  EVENT_CONTACT_FORM_WELCOME,
  EVENT_CONTACT_FORM_VALIDATION_BUTTON,
  EVENT_CONTACT_FORM_CHECKBOX_MESSAGE,
  EVENT_CONTACT_FORM_CHECKBOX_LINK,
} from "_constants";

import type { Event } from "event/types/event.model";
import { Header } from "./Header";
import {
  DEFAULT_RED_COLOR,
  DEFAULT_WHITE_COLOR,
} from "../../../_graphic_design";
import { Header2 } from "./Header2";
import { FormFieldTextInputV2 } from "common/form/FormFieldTextInputV2";

interface EventRegisterFormProps {
  event: Event;
}

export function EventRegisterFormV2({ event }: EventRegisterFormProps) {
  const [checked, setChecked] = useState<boolean>(false);
  const [checkboxError, setCheckboxError] = useState<string | null>(null);
  const initialValues: Partial<PlayerFormFields> = {
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    room: "",
    enterprise: "",
    termsOfService: checked,
  };
  const form = useForm({
    initialValues,
    validate: {
      firstName: (value: string) =>
        value ? null : FORM_VALIDATION_FIRST_NAME_REQUIRED, // Compulsory
      lastName: (value: string) =>
        value ? null : FORM_VALIDATION_LAST_NAME_REQUIRED, // Compulsory
      email: (value: string) =>
        value
          ? /^\S+@\S+$/.test(value)
            ? null
            : FORM_VALIDATION_EMAIL_NOT_VALIDATED
          : null, // Optional (only validates if provided)
      phoneNumber: (value: string) =>
        value && value.length >= 5 && /^(\d|\+|-| )+$/.test(value)
          ? null
          : FORM_VALIDATION_PHONE_NOT_VALIDATED, // Compulsory
      termsOfService: (value: boolean) =>
        value === true || value === false
          ? null
          : FORM_VALIDATION_TERMS_OF_SERVICE, // Optional (no validation if not provided)
      room: (value: string) =>
        value && value.length > 20 ? "Maximum 20 characters allowed" : null, // Optional
      enterprise: (value: string) =>
        value && value.length > 20 ? "Maximum 20 characters allowed" : null, // Optional
    },
  });

  const { eventId } = useParams<{ eventId: string }>();
  const setSavedEvent = useSavedEvent(eventId!)[1];
  const [addPlayer] = useAddPlayerMutation();

  const on_submit = async (values: Partial<PlayerFormFields>) => {
    const { bingo_game_id: bingoGameId } = event;
    const playerInfo: Partial<PlayerFormFields> = {
      ...values,
      termsOfService: checked,
    };
    const payload: PlayerCreationPayload = bingoGameId
      ? { bingoGameId, playerInfo }
      : { playerInfo };
    const newPlayer = await addPlayer(payload).then(throwError);
    setSavedEvent({
      playerId: newPlayer.id,
      partitionId: newPlayer.partitionId,
      playerInfo,
    });
  };

  const registerFormLabelsFontColor =
    event.colors?.registerFormLabels ?? DEFAULT_WHITE_COLOR;

  return (
    <Container
      className="min-h-screen flex flex-col items-center gap-x-16 -mb-5"
      pb={"lg"}
    >
      {event.header?.enabled ? <Header2 /> : <Header />}

      <Stack>
        <Text
          className="text-center mt-4"
          style={{
            color: registerFormLabelsFontColor,
            fontSize: event.labels?.login_title_size ?? "50px",
          }}
          dangerouslySetInnerHTML={{
            __html: event.labels?.register_form_welcome_msg
              ? event.labels.register_form_welcome_msg
              : EVENT_CONTACT_FORM_WELCOME,
          }}
        />
      </Stack>
      <br />
      <form onSubmit={form.onSubmit(on_submit)}>
        <Stack className="w-screen px-2.5" px={"xl"} align={"center"}>
          <FormFieldTextInputV2
            placeholder="Prénom"
            fontColor={registerFormLabelsFontColor}
            borderColor={event.colors?.inputBorderColor}
            name="firstName"
            type="string"
            isRequired
            form={form}
          />
          <FormFieldTextInputV2
            placeholder="Nom"
            fontColor={registerFormLabelsFontColor}
            borderColor={event.colors?.inputBorderColor}
            name="lastName"
            type="string"
            isRequired
            form={form}
          />
          {event.extra_fields_object?.email && (
            <FormFieldTextInputV2
              placeholder="Email"
              fontColor={registerFormLabelsFontColor}
              borderColor={event.colors?.inputBorderColor}
              name="email"
              type="string"
              isRequired
              form={form}
            />
          )}
          <FormFieldTextInputV2
            placeholder="Téléphone"
            fontColor={registerFormLabelsFontColor}
            borderColor={event.colors?.inputBorderColor}
            name="phoneNumber"
            type="tel"
            isRequired
            form={form}
          />
          {event.extra_fields_object?.room && (
            <FormFieldTextInputV2
              placeholder="Salon/Loge"
              fontColor={registerFormLabelsFontColor}
              borderColor={event.colors?.inputBorderColor}
              name="room"
              type="string"
              isRequired
              form={form}
            />
          )}
          {event.extra_fields_object?.enterprise && (
            <FormFieldTextInputV2
              placeholder="Société"
              fontColor={registerFormLabelsFontColor}
              borderColor={event.colors?.inputBorderColor}
              name="enterprise"
              type="string"
              isRequired
              form={form}
            />
          )}
          {event.extra_fields_object?.location1_enabled && (
            <FormFieldTextInputV2
              placeholder={event.extra_fields_object?.location1_name as string}
              fontColor={registerFormLabelsFontColor}
              borderColor={event.colors?.inputBorderColor}
              name={(event.extra_fields_object?.location1_name as string)
                .replace(/\s/g, "")
                .toLowerCase()}
              type="string"
              isRequired
              form={form}
            />
          )}
          {event.extra_fields_object?.location2_enabled && (
            <FormFieldTextInputV2
              placeholder={event.extra_fields_object?.location2_name as string}
              fontColor={registerFormLabelsFontColor}
              borderColor={event.colors?.inputBorderColor}
              name={(event.extra_fields_object?.location2_name as string)
                .replace(/\s/g, "")
                .toLowerCase()}
              type="string"
              isRequired
              form={form}
            />
          )}
          {event.extra_fields_object?.location3_enabled && (
            <FormFieldTextInputV2
              placeholder={event.extra_fields_object?.location3_name as string}
              fontColor={registerFormLabelsFontColor}
              borderColor={event.colors?.inputBorderColor}
              name={(event.extra_fields_object?.location3_name as string)
                .replace(/\s/g, "")
                .toLowerCase()}
              type="string"
              isRequired
              form={form}
            />
          )}
          {event.extra_fields_object?.termsandconditions && (
            <Checkbox
              key="termsOfService"
              name="termsOfService"
              required
              checked={checked}
              classNames={{
                root: "w-90",
                label: "pl-0",
                labelWrapper: "ml-3",
              }}
              label={
                <Text
                  className="leading-6 text-xs text-center" // Added text-center class
                  style={{
                    color: registerFormLabelsFontColor,
                    textAlign: "center", // Added text-align center
                  }}
                >
                  {event.labels?.register_form_checkbox_msg
                    ? event.labels?.register_form_checkbox_msg
                    : EVENT_CONTACT_FORM_CHECKBOX_MESSAGE}
                  <br />
                  <a
                    className="underline text-center" // Added text-center class
                    target="_blank"
                    rel="noreferrer"
                    href={event?.legalUrl || config.legalUrl}
                    style={{
                      textAlign: "center", // Added text-align center
                      display: "inline-block", // Ensure anchor respects text alignment
                    }}
                  >
                    {event.labels?.register_form_checkbox_link
                      ? event.labels?.register_form_checkbox_link
                      : EVENT_CONTACT_FORM_CHECKBOX_LINK}
                  </a>
                </Text>
              }
              error={checkboxError}
              color={"red"}
              {...form.getInputProps("termsOfService", { type: "checkbox" })}
              onClick={(event) => {
                setCheckboxError(null);
                return setChecked(event.currentTarget.checked);
              }}
            />
          )}
          <div>
            <Text
              className="text-center text-xs mx-0 my-px-5"
              style={{ color: registerFormLabelsFontColor }}
              dangerouslySetInnerHTML={{
                __html: event.labels?.register_form_thanks_msg
                  ? event.labels?.register_form_thanks_msg
                  : "",
              }}
            />
          </div>
          <div className="text-center">
            <Button
              type="submit"
              uppercase
              mx={"lg"}
              style={{
                backgroundColor:
                  event.colors?.registerFormSubmitButton ?? DEFAULT_RED_COLOR,
                borderColor:
                  event.colors?.registerFormSubmitButtonBorder ??
                  DEFAULT_WHITE_COLOR,
                borderWidth: "2px",
                color: registerFormLabelsFontColor,
                height: "45px",
                minWidth: "160px",
                borderRadius: "10px",
              }}
            >
              <span>
                {event.labels?.register_form_validation_button
                  ? event.labels?.register_form_validation_button
                  : EVENT_CONTACT_FORM_VALIDATION_BUTTON}
              </span>
            </Button>
          </div>
        </Stack>
      </form>
    </Container>
  );
}
